// replay-audio.ts — the sound stack shared by both replay viewers: the game's own SoundMgr
// (gsndm port) over htsfx.pdb (ADPCM sounds + the id→{sound,priority} table) mixed through
// WebAudioSoundDevice with the game's priority channel allocation. Handles the gesture-unlock
// dance (the wi-ios-audio-fix pattern), the transport-bar mute button (persisted), and the
// spectator palette: play(sfx, full=false) applies the battlefield-only allowlist; full=true
// (a fog side is toggled on → the re-sim's local player IS that side) plays the whole stream.
//
// The viewers own WHEN to play (live forward stepping only — seeks/prewarm stay silent).
import { MemPdbReader } from '../io/pdbreader.ts';
import { PackFileReader } from '../io/packfile.ts';
import { SoundMgr } from '../audio/soundmgr.ts';
import { WebAudioSoundDevice } from '../audio/webaudiodev.ts';
import { spectatorSfxAllowed } from './replay-sfx.ts';

export interface ReplayAudio {
  play(sfx: number | string, full: boolean): void;
  /** Tear the audio stack down (the EMBEDDED viewer's stop() — a page viewer dies with its page):
   *  unhook the gesture-unlock listeners and close the AudioContext so repeated open/close cycles
   *  don't accumulate live contexts (browsers cap them). */
  dispose(): void;
}

const MUTE_KEY = 'wiReplaySfx';

// the game's sfx pack — optional: a 404 (older deploy / local server) just means no audio
export function fetchSfxBytes(): Promise<Uint8Array | null> {
  return fetch('/htsfx.pdb')
    .then((r) => (r.ok ? r.arrayBuffer() : null))
    .then((b) => (b !== null ? new Uint8Array(b) : null))
    .catch(() => null);
}

export function initReplayAudio(
  sfxBytes: Uint8Array | null,
  btn: HTMLButtonElement | null,
): ReplayAudio {
  let sndm: SoundMgr | null = null;
  let sndDev: WebAudioSoundDevice | null = null;
  let sfxOn = localStorage.getItem(MUTE_KEY) !== 'off';
  const unlock = (): void => sndDev?.unlock();
  const onVisible = (): void => {
    if (document.visibilityState === 'visible') unlock();
  };
  if (sfxBytes !== null) {
    const rdrSfx = new MemPdbReader();
    const pakrSfx = new PackFileReader();
    if (rdrSfx.Open(sfxBytes) && pakrSfx.Push(null, 'htsfx.pdb', rdrSfx)) {
      const m = new SoundMgr(pakrSfx, {
        openSoundDevice: () => (sndDev = new WebAudioSoundDevice()),
      });
      if (m.Init()) {
        m.Enable(sfxOn); // the device starts disabled — the game enables from prefs after Init
        sndm = m;
        // the wi-ios-audio-fix unlock pattern (main.ts wireAudioUnlock): AudioContext starts
        // on a real user gesture; plays before the first unlock are dropped
        window.addEventListener('touchend', unlock);
        window.addEventListener('click', unlock);
        window.addEventListener('keydown', unlock);
        window.addEventListener('pointerup', unlock);
        window.addEventListener('pageshow', unlock);
        document.addEventListener('visibilitychange', onVisible);
      }
    }
  }
  const syncBtn = (): void => {
    if (btn === null) return;
    btn.textContent = sndm === null ? '\u{1F507}' : sfxOn ? '\u{1F50A}' : '\u{1F507}';
    btn.disabled = sndm === null;
  };
  syncBtn();
  btn?.addEventListener('click', () => {
    sfxOn = !sfxOn;
    localStorage.setItem(MUTE_KEY, sfxOn ? 'on' : 'off');
    sndm?.Enable(sfxOn); // also silences anything already scheduled on the channels
    syncBtn();
  });
  return {
    play(sfx: number | string, full: boolean): void {
      if (!sfxOn || sndm === null) return;
      if (!full && !spectatorSfxAllowed(sfx)) return;
      sndm.PlaySfx(sfx);
    },
    dispose(): void {
      window.removeEventListener('touchend', unlock);
      window.removeEventListener('click', unlock);
      window.removeEventListener('keydown', unlock);
      window.removeEventListener('pointerup', unlock);
      window.removeEventListener('pageshow', unlock);
      document.removeEventListener('visibilitychange', onVisible);
      sndm?.Enable(false); // silence anything scheduled on the channels
      sndDev?.teardownHidden(); // close the AudioContext (a later unlock() would reopen; none comes)
      sndm = null;
      sndDev = null;
    },
  };
}
